import Input from "@/components/Usados/InputField";
import Label from "@/components/Usados/Label";
import Button from "@/components/ui/button/Button";
import Alert from "@/components/ui/alert/Alert";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import Autocomplete from "@/components/Usados/AutoComplete";
import { apiFetch } from "@/service/api";

interface CreateEquipoProps {
  equipo?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function CreateEquipo({
  equipo,
  onSuccess,
  onCancel,
}: CreateEquipoProps) {
  const { id: proyecto_id } = useParams();

  const [form, setForm] = useState({
    proyecto_id: proyecto_id,
    actividad_id: 0,
    nombre: "",
    descripcion: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [actividades, setActividades] = useState([]);
  const isEdit = !!equipo;

  useEffect(() => {
    const fetchActividades = async () => {
      try {
        const response = await apiFetch(
          `actividades-proyectos/find-all-proyecto/${proyecto_id}`,
        );
        if (response.ok) {
          const data = await response.json();
          setActividades(
            data.map((a: any) => ({ value: a.id.toString(), label: a.nombre })),
          );
        }
      } catch (error) {
        console.error("Error fetching actividades:", error);
      }
    };
    fetchActividades();
  }, [proyecto_id]);

  useEffect(() => {
    if (equipo) {
      setForm({
        proyecto_id: proyecto_id,
        actividad_id: equipo.actividad?.id || 0,
        nombre: equipo.nombre || "",
        descripcion: equipo.descripcion || "",
      });
    } else {
      resetForm();
    }
  }, [equipo, proyecto_id]);

  const resetForm = () =>
    setForm({
      proyecto_id: proyecto_id,
      actividad_id: 0,
      nombre: "",
      descripcion: "",
    });
  const handleCancel = () => {
    resetForm();
    if (onCancel) onCancel();
  };

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const url = isEdit
      ? `equipos-proyectos/update/${equipo.id}`
      : `equipos-proyectos/create`;
    const method = isEdit ? "PATCH" : "POST";
    try {
      const response = await apiFetch(url, {
        method,
        body: JSON.stringify({
          ...form,
          proyecto_id: Number(form.proyecto_id),
          actividad_id: Number(form.actividad_id) || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Error al ${isEdit ? "actualizar" : "crear"} equipo`,
        );
      }
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        if (onSuccess) onSuccess();
      }, 800);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="min-h- rounded-2xl border border-gray-200 bg-white px-5 py-7 dark:border-gray-800 dark:bg-white/[0.03] xl:px-10 xl:py-12">
        <h3 className="mb-8 font-semibold text-gray-800 text-center text-theme-xl dark:text-white/90 sm:text-2xl">
          {isEdit
            ? "Editar Equipo de Proyecto"
            : "Crear Nuevo Equipo de Proyecto"}
        </h3>
        {error && <Alert variant="error" title="Error" message={error} />}
        {success && (
          <Alert
            variant="success"
            title="exito"
            message={`Equipo ${
              isEdit ? "actualizado" : "creado"
            } correctamente`}
          />
        )}
        <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
          <div>
            <Label>Nombre del Equipo</Label>
            <Input
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              required
              placeholder="Ej: Equipo de Desarrollo Frontend"
            />
          </div>
          <div>
            <Label>Descripcion</Label>
            <textarea
              name="descripcion"
              value={form.descripcion}
              onChange={handleChange}
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2 text-gray-800 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:text-white/90"
              rows={3}
              required
            />
          </div>
          <div>
            <Autocomplete
              label="Actividad Asociada (Opcional)"
              options={actividades}
              value={form.actividad_id?.toString()}
              placeholder="Seleccionar actividad"
              onChange={(value) =>
                setForm({ ...form, actividad_id: Number(value) })
              }
            />
          </div>
          <div className="flex justify-end gap-4 mt-8">
            {onCancel && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading
                ? isEdit
                  ? "Actualizando..."
                  : "Creando..."
                : isEdit
                ? "Actualizar Equipo"
                : "Crear Equipo"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
